import { ErrorMessage, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Dialog } from '../../components/common';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { InputLabel, FieldFormTextInput } from '../../components/forms';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { PLACEHOLDER_TEXT } from '../../constants/placeholders';
import { SUCCESSFUL_REQUEST_CODE } from '../../utils';
import { officerServices } from '../../services/officerServices';
import { TOAST_TYPES } from '../../components/ResponseAlert';
import { AsyncPaginate } from 'react-select-async-paginate';
import { AsyncPaginateGetOptions } from '../../utils/asyncPaginateGetOptions';

function OfficerForm({
	showModal = false,
	type,
	formData,
	setShowModal,
	setToast,
	onSuccessCallBack
}) {
	const { getAgenciesData } = AsyncPaginateGetOptions();
	const { postOfficerData, putOfficerData } = officerServices();
	const [isLoadingApi, setIsLoadingApi] = useState(false);
	const [initialValues, setInitialValues] = useState({
		lastName: '',
		badgenumber: '',
		firstName: '',
		middleName: '',
		suffix: '',
		agency: ''
	});

	const VALIDATION_SCHEMA = Yup.object().shape({
		firstName: Yup.string().max(255, '').required('First Name is required'),
		lastName: Yup.string().max(255, '').required('Last Name is required'),
		agency: Yup.object()
			.shape({
				value: Yup.string().required('Agency is required')
			})
			.nullable()
			.required('Agency is required'), // Explicitly handle the outer object
		badgenumber: Yup.string().max(255, '').required('Badge Number is required')
	});

	const onCloseModal = () => {
		setShowModal(false);
	};
	const title = type === 'ADD' ? 'New Officer' : 'Edit Officer';
	const createOfficerData = async (body) => {
		try {
			setIsLoadingApi(true);
			const response = await postOfficerData(body);
			if (SUCCESSFUL_REQUEST_CODE.includes(response.status)) {
				setToast({
					show: true,
					type: TOAST_TYPES.SUCCESS,
					text: 'New officer created successfully'
				});
				onSuccessCallBack();
				onCloseModal();
			}
			setIsLoadingApi(false);
		} catch (error) {
			setIsLoadingApi(false);
			setToast({
				show: true,
				type: TOAST_TYPES.DANGER,
				text: 'Unable to create new officer.'
			});
			onCloseModal();
		}
	};

	const updateOfficerForm = async (officerId, body) => {
		try {
			setIsLoadingApi(true);
			const resposne = await putOfficerData(officerId, body);
			if (SUCCESSFUL_REQUEST_CODE.includes(resposne.status)) {
				setToast({
					show: true,
					type: TOAST_TYPES.SUCCESS,
					text: 'Officer update successfully'
				});
				onSuccessCallBack();
				onCloseModal();
			}
			setIsLoadingApi(false);
		} catch (error) {
			setIsLoadingApi(false);
			setToast({
				show: true,
				type: TOAST_TYPES.DANGER,
				text: 'Unable to update the officer'
			});
			onCloseModal();
		}
	};

	const handleSubmitForm = (values) => {
		if (type === 'ADD') {
			const body = {
				badgeNo: values?.badgenumber,
				active: true,
				arrestingAgencyId: values?.agency?.value || null,
				newPersonDTO: {
					lastName: values.lastName,
					firstName: values.firstName,
					middleName: values.middleName,
					suffix: values.suffix,
					classification: 'Person'
				}
			};
			createOfficerData(body);
		} else if (type === 'EDIT') {
			const body = {
				badgeNo: values.badgenumber,
				active: true,
				arrestingAgencyId: values?.agency?.value || null,
				newPersonDTO: {
					lastName: values.lastName,
					firstName: values.firstName,
					middleName: values.middleName,
					suffix: values.suffix,
					classification: 'Person',
					personId: values?.personId
				}
			};
			updateOfficerForm(formData?.officerId, body);
		}
	};
	useEffect(() => {
		if (formData && type === 'ADD') {
			setInitialValues({
				lastName: '',
				firstName: '',
				middleName: '',
				suffix: '',
				badgenumber: '',
				agency: null,
				personId: ''
			});
		} else if (formData && type === 'EDIT') {
			setInitialValues({
				lastName: formData?.newPersonDTO?.lastName || '',
				firstName: formData?.newPersonDTO?.firstName || '',
				middleName: formData?.newPersonDTO?.middleName || '',
				suffix: formData?.newPersonDTO?.suffix || '',
				badgenumber: formData?.badgeNo || '',
				agency: formData?.arrestingAgencyOriDTO
					? {
							label: `${formData.arrestingAgencyOriDTO.agencyOri} ${formData.arrestingAgencyOriDTO.agencyName}`,
							value: formData.arrestingAgencyOriDTO.arrestingAgencyOriId
					  }
					: null,
				personId: formData.newPersonDTO?.personId
			});
		}
	}, [formData, type]);
	return (
		<Formik
			initialValues={initialValues}
			onSubmit={handleSubmitForm}
			enableReinitialize
			validationSchema={VALIDATION_SCHEMA}
		>
			{({ handleSubmit, handleReset, dirty, setFieldValue, values }) => {
				return (
					<Dialog
						isShow={showModal}
						onClose={() => {
							onCloseModal();
							handleReset();
						}}
						isAForm
						submitDisabled={!dirty && type === 'EDIT'}
						formId="officer-maintenance-form"
						title={title}
						onCancelButton={() => {
							onCloseModal();
							handleReset();
						}}
						onConfirm={() => {
							handleSubmit();
						}}
						isLoadingConfirm={isLoadingApi}
					>
						<Container>
							<Row>
								<Row className="mb-3">
									<Col xs={12} md={6}>
										<FieldFormTextInput
											id="lastName"
											name="lastName"
											label="Last  Name"
											required
											placeholder={`${PLACEHOLDER_TEXT} Last Name`}
										/>
									</Col>
									<Col xs={12} md={6} className="mb-3">
										<FieldFormTextInput
											id="firstName"
											name="firstName"
											label="First Name"
											required
											placeholder={`${PLACEHOLDER_TEXT} First Name`}
										/>
									</Col>
									<Col xs={12} md={6} className="mb-3">
										<FieldFormTextInput
											id="middleName"
											name="middleName"
											label="Middle Name"
											placeholder={`${PLACEHOLDER_TEXT} Middle Name`}
										/>
									</Col>
									<Col xs={12} md={6} className="mb-3">
										<InputLabel htmlFor="agency" required={true} name="Agency"></InputLabel>
										<AsyncPaginate
											id="agency"
											name="agency"
											aria-label="Agency"
											placeholder="- Select -"
											loadOptions={getAgenciesData}
											value={values?.agency || null}
											isClearable
											required
											additional={{
												pageNumber: 0
											}}
											onChange={(selected) => {
												setFieldValue('agency', selected || null);
											}}
											isSearchable
										></AsyncPaginate>
										<ErrorMessage
											render={(msg) => (
												<span role="alert" className="small text-danger">
													{msg}
												</span>
											)}
											name="agency"
										/>
									</Col>
									<Col xs={12} md={6} className="mb-3">
										<FieldFormTextInput
											id="badgenumber"
											name="badgenumber"
											label="Badge Number"
											required
											placeholder={`${PLACEHOLDER_TEXT} Badge Number`}
										/>
									</Col>
									<Col xs={12} md={6} className="mb-3">
										<FieldFormTextInput
											id="suffix"
											label="Suffix"
											placeholder={`${PLACEHOLDER_TEXT} suffix`}
										/>
									</Col>
								</Row>
							</Row>
						</Container>
					</Dialog>
				);
			}}
		</Formik>
	);
}

export default OfficerForm;

OfficerForm.propTypes = {
	showModal: PropTypes.bool,
	setShowModal: PropTypes.func,
	FormData: PropTypes.object,
	type: PropTypes.string,
	setToast: PropTypes.func,
	onSuccessCallBack: PropTypes.func
};
